import { useEffect, useRef, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useCollections } from '../contexts/ThreadsContext';

/**
 * 컬렉션 초기화를 안전하게 처리하는 커스텀 훅
 * Firebase 초기화, 인증 상태, 타이밍 문제를 모두 처리
 */
export const useCollectionInit = () => {
  const { profile } = useUser();
  const { loadCollections, collections, isLoading, error } = useCollections();
  const [initStatus, setInitStatus] = useState('idle'); // idle, loading, success, error
  const hasInitialized = useRef(false);
  const retryCount = useRef(0);
  const maxRetries = 3;

  useEffect(() => {
    const initCollections = async () => {
      // 이미 초기화됨 (collection이 0개여도 초기화 완료로 처리)
      if (hasInitialized.current && initStatus === 'success') {
        return;
      }

      // userId 확인
      const userId = profile?.id || localStorage.getItem("userID");
      if (!userId) {
        console.log("[useCollectionInit] No userId available yet");
        return;
      }

      // 이미 로딩 중
      if (initStatus === 'loading') {
        return;
      }

      console.log("[useCollectionInit] Initializing collections for userId:", userId);
      setInitStatus('loading');

      try {
        // Firebase가 초기화될 때까지 약간 대기
        if (!hasInitialized.current) {
          // Firebase 초기화 확인
          const checkFirebase = () => {
            const { dbService } = require('../firebaseInstance');
            return !!dbService;
          };
          
          let firebaseReady = checkFirebase();
          let attempts = 0;
          
          while (!firebaseReady && attempts < 10) {
            await new Promise(resolve => setTimeout(resolve, 200));
            firebaseReady = checkFirebase();
            attempts++;
          }
          
          if (!firebaseReady) {
            throw new Error("Firebase not initialized after waiting");
          }
        }

        await loadCollections(userId);
        hasInitialized.current = true;
        setInitStatus('success');
        retryCount.current = 0;
        console.log("[useCollectionInit] Collections initialized successfully");
      } catch (error) {
        console.error("[useCollectionInit] Failed to initialize collections:", error);
        
        // 재시도 로직
        if (retryCount.current < maxRetries) {
          retryCount.current++;
          console.log(`[useCollectionInit] Retrying... (${retryCount.current}/${maxRetries})`);
          
          // 지수 백오프로 재시도
          const delay = Math.min(1000 * Math.pow(2, retryCount.current - 1), 5000);
          setTimeout(() => {
            setInitStatus('idle'); // 재시도 가능하도록 상태 리셋
          }, delay);
        } else {
          setInitStatus('error');
          console.error("[useCollectionInit] Max retries reached. Giving up.");
        }
      }
    };

    initCollections();
  }, [profile?.id, initStatus, loadCollections]);

  // localStorage 변경 감지 (다른 탭에서 로그인한 경우)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userID' && e.newValue && !hasInitialized.current) {
        console.log("[useCollectionInit] Detected userID change in localStorage");
        setInitStatus('idle');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    collections,
    isLoading: isLoading || initStatus === 'loading',
    error,
    initStatus,
    hasInitialized: hasInitialized.current,
    loadCollections,
    retry: () => {
      console.log("[useCollectionInit] Retry called - resetting state");
      retryCount.current = 0;
      hasInitialized.current = false;
      setInitStatus('idle');
    }
  };
};